import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import EngagementPlaybook from "../components/EngagementPlaybook";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Instagram, Linkedin, Twitter, Facebook, Music2, Sparkles, Loader2, Send, EyeOff, Eye, ShieldAlert, CheckCheck, RefreshCw, Flame } from "lucide-react";

const ICONS = { instagram: Instagram, linkedin: Linkedin, twitter: Twitter, facebook: Facebook, tiktok: Music2 };
const SENTIMENT = { positive: "bg-accent/20 text-accent-foreground dark:text-accent", negative: "bg-destructive/15 text-destructive", toxic: "bg-destructive/15 text-destructive", spam: "bg-destructive/15 text-destructive", neutral: "bg-muted text-muted-foreground" };

export default function Inbox() {
  const { selected } = useBrand();
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [bulkDrafting, setBulkDrafting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ws, setWs] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setWs(r.data)); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/inbox/sync");
      toast.success(`Synced — ${data.imported} new message${data.imported === 1 ? "" : "s"}${data.errors?.length ? ` · ${data.errors.length} platform(s) unavailable` : ""}`);
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSyncing(false);
    }
  };

  const load = (f = filter) => api.get("/inbox", { params: { ...(f === "all" ? {} : { filter: f }), ...(selected ? { brand: selected } : {}) } }).then((r) => setMessages(r.data));
  useEffect(() => { setSel(null); load(filter); }, [filter, selected]);

  const [livePosts, setLivePosts] = useState([]);
  const loadLivePosts = () =>
    api.get("/posts", { params: { status: "published", ...(selected ? { brand: selected } : {}) } })
      .then((r) => {
        const sorted = [...r.data].sort(
          (a, b) => new Date(b.published_at || b.scheduled_at || 0) - new Date(a.published_at || a.scheduled_at || 0));
        setLivePosts(sorted.slice(0, 3));
      }).catch(() => {});
  useEffect(() => { loadLivePosts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selected]);
  const updateLivePost = (p) => setLivePosts((prev) => prev.map((x) => (x.id === p.id ? p : x)));

  // Live "golden 10 minutes" alert — posts published within the last 10 min.
  const activeLive = livePosts.filter((p) => {
    const start = p.published_at || p.scheduled_at;
    if (!start) return false;
    const elapsed = (Date.now() - new Date(start).getTime()) / 1000;
    return elapsed >= 0 && elapsed < 600;
  });
  // Ping once per post the moment it goes live (dedup via localStorage).
  useEffect(() => {
    if (activeLive.length === 0) return;
    let seen;
    try { seen = new Set(JSON.parse(localStorage.getItem("playbook_alerts_seen") || "[]")); }
    catch { seen = new Set(); }
    let changed = false;
    activeLive.forEach((p) => {
      if (seen.has(p.id)) return;
      seen.add(p.id); changed = true;
      toast(`"${p.title}" just went live — you're in the golden 10 minutes!`, {
        description: "Run your engagement playbook before the window closes.",
        duration: 12000,
        action: {
          label: "Run playbook",
          onClick: () => document.querySelector('[data-testid="inbox-playbook-banner"]')
            ?.scrollIntoView({ behavior: "smooth", block: "center" }),
        },
      });
    });
    if (changed) localStorage.setItem("playbook_alerts_seen", JSON.stringify([...seen]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePosts]);

  useEffect(() => {
    const t = setInterval(() => { load(filter); loadLivePosts(); api.get("/settings").then((r) => setWs(r.data)); }, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selected]);

  const select = (m) => { setSel(m); setReply(m.ai_draft || ""); };

  const aiDraft = async (tone = "friendly") => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/inbox/${sel.id}/ai-draft`, { tone });
      setReply(data.ai_draft);
      setMessages((prev) => prev.map((m) => (m.id === sel.id ? { ...m, ai_draft: data.ai_draft } : m)));
      toast.success(`AI reply drafted (${tone})`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setDrafting(false);
    }
  };

  const autoDraftAll = async (tone = "friendly") => {
    setBulkDrafting(true);
    try {
      const { data } = await api.post("/inbox/auto-draft-all", { tone }, { params: selected ? { brand: selected } : {} });
      toast.success(data.drafted ? `AI drafted ${data.drafted} ${tone} repl${data.drafted === 1 ? "y" : "ies"} — review & send` : "Nothing awaiting a draft");
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBulkDrafting(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.post(`/inbox/${sel.id}/reply`, { text: reply });
    setMessages((prev) => prev.map((m) => (m.id === sel.id ? { ...m, replied: true, reply_text: reply } : m)));
    setSel({ ...sel, replied: true, reply_text: reply });
    toast.success("Reply sent (simulated publish to platform)");
  };

  const toggleHide = async (m) => {
    await api.patch(`/inbox/${m.id}/toggle-hide`);
    load();
    if (sel?.id === m.id) setSel({ ...sel, hidden: !sel.hidden });
  };

  return (
    <div className="space-y-6 fade-up" data-testid="inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">Unified Inbox{selected ? ` · ${selected}` : ""}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{selected ? `${selected} conversations.` : "Every conversation, one place."}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {(ws?.native_meta_connected || ws?.native_x_connected || ws?.native_threads_connected) && (
            <>
              <span className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="auto-sync-indicator">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" /> Auto-sync every 5 min{ws.last_inbox_sync ? ` · last ${new Date(ws.last_inbox_sync).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
              </span>
              <Button size="sm" variant="outline" data-testid="inbox-sync-btn" disabled={syncing} onClick={syncNow} title="Pull real comments & DMs">
                {syncing ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <RefreshCw size={13} className="mr-1.5" />} Sync
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid="auto-draft-all-btn" disabled={bulkDrafting}>
                {bulkDrafting ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Sparkles size={13} className="mr-1.5" />} Auto-draft all
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">Pick a tone for every draft</DropdownMenuLabel>
              <DropdownMenuItem data-testid="auto-draft-all-friendly" onClick={() => autoDraftAll("friendly")}>Friendly · warm & welcoming</DropdownMenuItem>
              <DropdownMenuItem data-testid="auto-draft-all-professional" onClick={() => autoDraftAll("professional")}>Professional · clear & polished</DropdownMenuItem>
              <DropdownMenuItem data-testid="auto-draft-all-witty" onClick={() => autoDraftAll("witty")}>Witty · playful & clever</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="unreplied" data-testid="filter-unreplied">Awaiting reply</TabsTrigger>
              <TabsTrigger value="flagged" data-testid="filter-flagged">Flagged</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {activeLive.length > 0 && (
        <div data-testid="playbook-live-alert"
          className="flex items-center gap-3 rounded-xl border border-accent bg-accent/10 px-4 py-3">
          <Flame className="text-accent shrink-0 animate-pulse" size={18} />
          <p className="text-sm font-medium">
            {activeLive.length} post{activeLive.length > 1 ? "s" : ""} just went live — you're in the golden 10 minutes. Run your playbook now.
          </p>
          <Button size="sm" variant="outline" className="ml-auto shrink-0" data-testid="playbook-live-alert-cta"
            onClick={() => document.querySelector('[data-testid="inbox-playbook-banner"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}>
            Run playbook
          </Button>
        </div>
      )}

      {livePosts.length > 0 && (
        <div data-testid="inbox-playbook-banner" className="space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
            Recently published · run your engagement playbook
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {livePosts.map((p) => (
              <div key={p.id} className="space-y-1.5">
                <p className="text-xs font-medium truncate flex items-center gap-1.5" title={p.title}>
                  {(() => { const I = ICONS[p.platform]; return I ? <I size={12} className="text-muted-foreground shrink-0" /> : null; })()}
                  {p.title}
                </p>
                <EngagementPlaybook post={p} onUpdate={updateLivePost} compact />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 items-start">
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="inbox-list">
          {messages.map((m) => {
            const Icon = ICONS[m.platform] || Instagram;
            return (
              <button key={m.id} data-testid={`inbox-msg-${m.id}`} onClick={() => select(m)}
                className={`w-full text-left p-4 rounded-xl border transition-colors ${sel?.id === m.id ? "border-accent bg-secondary/60" : "border-border bg-card hover:bg-muted"} ${m.hidden ? "opacity-50" : ""}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="flex items-center gap-2 text-sm font-semibold truncate">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-2/15 text-accent-2 shrink-0" title={m.platform} data-testid={`inbox-platform-tag-${m.id}`}>
                      <Icon size={13} />
                    </span>
                    {m.author}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {m.brand && !selected && <Badge variant="secondary" className="text-[9px] max-w-[90px] truncate">{m.brand}</Badge>}
                    {m.flagged && <ShieldAlert size={13} className="text-destructive" />}
                    {m.replied && <CheckCheck size={13} className="text-accent" />}
                    {m.ai_draft && !m.replied && <Sparkles size={13} className="text-accent" />}
                    <Badge variant="outline" className="text-[9px] uppercase">{m.type}</Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{m.content}</p>
              </button>
            );
          })}
          {messages.length === 0 && <p className="text-sm text-muted-foreground p-4">Nothing here.</p>}
        </div>

        <div className="rounded-xl border border-border bg-card p-6 min-h-[420px]" data-testid="inbox-detail">
          {!sel ? (
            <p className="text-muted-foreground text-sm flex items-center justify-center h-full">Select a message to view & reply.</p>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold">{sel.author}</p>
                  <p className="text-xs text-muted-foreground capitalize">{sel.platform} · {sel.type} · {new Date(sel.created_at).toLocaleString()}{sel.brand ? <> · <span className="text-accent font-semibold">{sel.brand}</span></> : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`capitalize border-0 ${SENTIMENT[sel.sentiment] || SENTIMENT.neutral}`}>{sel.sentiment}</Badge>
                  <Button size="sm" variant="outline" data-testid="toggle-hide-btn" onClick={() => toggleHide(sel)}>
                    {sel.hidden ? <><Eye size={13} className="mr-1" /> Unhide</> : <><EyeOff size={13} className="mr-1" /> Hide</>}
                  </Button>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted text-sm leading-relaxed">{sel.content}</div>
              {sel.flagged && (
                <p className="text-xs text-destructive flex items-center gap-1.5"><ShieldAlert size={13} /> Auto-flagged by moderation filter as {sel.sentiment}.</p>
              )}
              {sel.replied ? (
                <div className="p-4 rounded-lg bg-secondary text-sm">
                  <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><CheckCheck size={13} /> Your reply</p>
                  {sel.reply_text}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Reply</p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="ai-draft-btn" disabled={drafting}>
                          {drafting ? <Loader2 size={13} className="animate-spin mr-1" /> : <Sparkles size={13} className="mr-1" />} AI draft reply
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel className="text-xs">Draft this reply as…</DropdownMenuLabel>
                        <DropdownMenuItem data-testid="ai-draft-friendly" onClick={() => aiDraft("friendly")}>Friendly</DropdownMenuItem>
                        <DropdownMenuItem data-testid="ai-draft-professional" onClick={() => aiDraft("professional")}>Professional</DropdownMenuItem>
                        <DropdownMenuItem data-testid="ai-draft-witty" onClick={() => aiDraft("witty")}>Witty</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <Textarea data-testid="reply-input" rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply — or let AI draft one grounded in your brand knowledge..." />
                  <Button data-testid="send-reply-btn" onClick={sendReply} disabled={!reply.trim()}><Send size={14} className="mr-2" /> Send reply</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
